import React, { useState, useRef, useEffect } from 'react';
import { collection, addDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../../firebaseDDH';
import PageHero from '../../components/shared/PageHero';
import { MONO, SANS } from '../../constants/fonts';
import heroPhoto from '../../assets/DDHAPPS/white-top-talking.webp';
import { contactEmail } from '../../utils/email';

const NAME_PLACEHOLDER = Math.random() < 0.5 ? 'David Dobson' : "Hugo A'Violet";

const PROJECT_TYPES = ['Website', 'Mobile app', 'Both', 'Not sure yet'];
const CONTACT_METHODS = ['Email', 'Phone', 'Text', 'WhatsApp'];

const EMPTY = {
  name: '',
  company: '',
  email: '',
  phone: '',
  countryCode: '+44',
  projectTypes: [],
  description: '',
  questions: '',
  preferredContact: [],
  extra: '',
};

const STEPS = ['about', 'project', 'contact'];
const STEP_LABELS = ['About you', 'Your project', 'Final details'];

const heroBadge = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 10,
  background: '#fff',
  border: '1px solid #e5e5ea',
  borderRadius: 32,
  padding: '10px 20px',
  fontSize: 'clamp(0.72rem, 2vw, 0.82rem)',
  color: '#6b6b6b',
  boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
  maxWidth: 520,
  textAlign: 'left',
  lineHeight: 1.5,
};

const heroBadgeLink = { color: '#4338ca', fontWeight: 600, textDecoration: 'none' };

const STORAGE_KEY = 'ddhapps_contact_draft';

function ContactScreen() {
  const [step, setStep] = useState(() => {
    try { return JSON.parse(localStorage.getItem(STORAGE_KEY))?.step ?? 0; } catch { return 0; }
  });
  const [fields, setFields] = useState(() => {
    try { return { ...EMPTY, ...JSON.parse(localStorage.getItem(STORAGE_KEY))?.fields }; } catch { return EMPTY; }
  });
  const [errors, setErrors] = useState({});
  const [status, setStatus] = useState('idle');
  const firstInputRef = useRef(null);

  useEffect(() => {
    if (firstInputRef.current && !('ontouchstart' in window)) {
      firstInputRef.current.focus({ preventScroll: true });
    }
  }, [step]);

  useEffect(() => {
    try { localStorage.setItem(STORAGE_KEY, JSON.stringify({ step, fields })); } catch {}
  }, [step, fields]);

  const set = (key, value) => {
    setFields(f => ({ ...f, [key]: value }));
    if (errors[key]) setErrors(e => ({ ...e, [key]: null }));
  };

  const toggleProjectType = (type) => {
    setFields(f => ({
      ...f,
      projectTypes: f.projectTypes.includes(type)
        ? f.projectTypes.filter(t => t !== type)
        : [...f.projectTypes, type],
    }));
  };

  const validateStep = () => {
    if (step === 0) {
      const e = {};
      if (!fields.name.trim()) e.name = 'Please enter your name.';
      if (!fields.email.trim()) e.email = 'Please enter your email.';
      else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(fields.email)) e.email = 'Please enter a valid email.';
      return e;
    }
    return {};
  };

  const handleNext = () => {
    const errs = validateStep();
    if (Object.keys(errs).length) { setErrors(errs); return; }
    setStep(s => s + 1);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (step < STEPS.length - 1) { handleNext(); return; }

    setStatus('submitting');
    try {
      await addDoc(collection(db, 'enquiries'), { ...fields, submittedAt: serverTimestamp() });
      setStatus('success');
      setFields(EMPTY);
      setStep(0);
      try { localStorage.removeItem(STORAGE_KEY); } catch {}
    } catch (err) {
      console.error('Firestore submit error:', err);
      setStatus('error');
    }
  };

  if (status === 'success') {
    return (
      <div style={s.page}>
        <PageHero
          eyebrow="Get in touch"
          headline={<>Tell us about<br />your project.</>}
          image={heroPhoto}
          sub="Fill in the form below and we'll get back to you as soon as we can. No pushy sales, just an honest conversation."
          badge={
            <div style={heroBadge}>
              <span style={{ width: 7, height: 7, borderRadius: '50%', background: '#34d399', flexShrink: 0 }} />
              <span>Prefer to skip the form?{' '}<span style={{ whiteSpace: 'nowrap' }}>Email us at{' '}<a href={`mailto:${contactEmail()}`} style={heroBadgeLink}>{contactEmail()}</a></span></span>
            </div>
          }
        />
        <section style={s.formSection}>
          <div style={s.formCard}>
            <div style={s.successBox}>
              <div style={s.successIcon}>✓</div>
              <h2 style={s.successHeadline}>Message received.</h2>
              <p style={s.successSub}>Thanks for reaching out. We'll be in touch as soon as we can.</p>
              <button onClick={() => { setStatus('idle'); setStep(0); try { localStorage.removeItem(STORAGE_KEY); } catch {} }} style={s.resetBtn}>
                Send another message
              </button>
            </div>
          </div>
        </section>
      </div>
    );
  }

  return (
    <div style={s.page}>
      <PageHero
        eyebrow="Get in touch"
        headline={<>Tell us about<br />your project.</>}
        image={heroPhoto}
        sub="Fill in the form below and we'll get back to you as soon as we can. No pushy sales, just an honest conversation."
        badge={
          <div style={heroBadge}>
            <span style={{ width: 7, height: 7, borderRadius: '50%', background: '#34d399', flexShrink: 0 }} />
            <span>Prefer to skip the form?{' '}<span style={{ whiteSpace: 'nowrap' }}>Email us at{' '}<a href={`mailto:${contactEmail()}`} style={heroBadgeLink}>{contactEmail()}</a></span></span>
          </div>
        }
      />

      <section className="contact-section" style={s.formSection}>
        <style>{`
          @media (max-width: 600px) {
            .contact-card { min-height: unset !important; padding: 20px 16px !important; }
            .contact-section { padding-left: 12px !important; padding-right: 12px !important; }
            .contact-form-fields { justify-content: flex-start !important; gap: 20px; }
            .contact-form-fields > div { margin-bottom: 0 !important; }
          }
        `}</style>
        <div style={s.formTitle}>
          <p style={s.formTitleEyebrow}>Work with us</p>
          <h2 style={s.formTitleHeading}>Let's build something great.</h2>
          <p style={s.formTitleSub}>Don't worry, the form won't take long.</p>
        </div>
<div className="contact-card" style={s.formCard}>

          <div style={s.stepHeader}>
            <div style={{ display: 'flex', alignItems: 'baseline', gap: 10 }}>
              <span style={s.stepCount}>Step {step + 1} of {STEPS.length}</span>
              <span style={s.stepTitle}>{STEP_LABELS[step]}</span>
            </div>
            <div style={s.dots}>
              {STEPS.map((_, i) => (
                <div key={i} style={{ ...s.dot, background: i <= step ? '#818cf8' : 'rgba(255,255,255,0.1)' }} />
              ))}
            </div>
          </div>

          <form onSubmit={handleSubmit} noValidate style={{ display: 'flex', flexDirection: 'column', flex: 1, paddingBottom: 64 }}>

            {step === 0 && (
              <div className="contact-form-fields" style={{ display: 'flex', flexDirection: 'column', flex: 1, justifyContent: 'space-between' }}>
                <div style={s.row2}>
                  <div>
                    <p style={s.question}>What's your name?</p>
                    <div style={{ ...s.fieldGroup, marginBottom: 0 }}>
                      <input
                        ref={firstInputRef}
                        className="contact-input" style={{ ...s.input, ...(errors.name ? s.inputError : {}) }}
                        placeholder={NAME_PLACEHOLDER}
                        value={fields.name}
                        onChange={e => set('name', e.target.value)}
                      />
                      {errors.name && <p style={s.fieldError}>{errors.name}</p>}
                    </div>
                  </div>
                  <div>
                    <p style={s.question}>Company <span style={s.optional}>Optional</span></p>
                    <div style={{ ...s.fieldGroup, marginBottom: 0 }}>
                      <input
                        className="contact-input" style={s.input}
                        placeholder="DDHAPPS LTD."
                        value={fields.company}
                        onChange={e => set('company', e.target.value)}
                      />
                    </div>
                  </div>
                </div>
                <div>
                  <p style={s.question}>And your email?</p>
                  <div style={{ ...s.fieldGroup, marginBottom: 0 }}>
                    <input
                      type="email"
                      className="contact-input" style={{ ...s.input, ...(errors.email ? s.inputError : {}) }}
                      placeholder="hello@ddhapps.com"
                      value={fields.email}
                      onChange={e => set('email', e.target.value)}
                    />
                    {errors.email && <p style={s.fieldError}>{errors.email}</p>}
                  </div>
                </div>
                <div>
                  <p style={s.question}>Phone <span style={s.optional}>Optional</span></p>
                  <div style={{ ...s.fieldGroup, display: 'flex', gap: 10, marginBottom: 0 }}>
                  <input
                    type="text"
                    className="contact-input" style={{ ...s.input, width: 60, flexShrink: 0 }}
                    placeholder="+44"
                    value={fields.countryCode}
                    onChange={e => set('countryCode', e.target.value)}
                  />
                  <input
                    type="tel"
                    className="contact-input" style={{ ...s.input, flex: 1 }}
                    placeholder="7700 000000"
                    value={fields.phone}
                    onChange={e => set('phone', e.target.value)}
                  />
                  </div>
                </div>
                <div>
                  <p style={s.question}>How would you prefer we get in touch?</p>
                  <div style={{ ...s.fieldGroup, ...s.chipRowScroll, marginBottom: 0 }}>
                    {CONTACT_METHODS.map(m => {
                      const active = fields.preferredContact.includes(m);
                      return (
                        <button
                          key={m}
                          type="button"
                          onClick={() => set('preferredContact', active ? fields.preferredContact.filter(x => x !== m) : [...fields.preferredContact, m])}
                          style={{ ...s.chip, ...(active ? s.chipActive : {}), display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0 }}
                        >
                          {m}
                          <svg width="13" height="13" viewBox="0 0 13 13" fill="none">
                            <rect x="0.5" y="0.5" width="12" height="12" rx="3" stroke={active ? '#818cf8' : 'rgba(255,255,255,0.2)'} fill={active ? '#818cf8' : 'none'} />
                            {active && <polyline points="2.5,6.5 5.5,9.5 10.5,3.5" stroke="#fff" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />}
                          </svg>
                        </button>
                      );
                    })}
                  </div>
                </div>
              </div>
            )}

            {step === 1 && (
              <div style={{ display: 'flex', flexDirection: 'column', flex: 1 }}>
                <p style={s.question}>What are you looking for? <span style={s.optional}>Optional</span></p>
                <div style={{ ...s.fieldGroup, ...s.chipRow }}>
                  {PROJECT_TYPES.map(type => {
                    const active = fields.projectTypes.includes(type);
                    return (
                      <button
                        key={type}
                        type="button"
                        onClick={() => toggleProjectType(type)}
                        style={{ ...s.chip, ...(active ? s.chipActive : {}), display: 'flex', alignItems: 'center', gap: 6 }}
                      >
                        {type}
                        <svg width="13" height="13" viewBox="0 0 13 13" fill="none">
                          <rect x="0.5" y="0.5" width="12" height="12" rx="3" stroke={active ? '#818cf8' : 'rgba(255,255,255,0.2)'} fill={active ? '#818cf8' : 'none'} />
                          {active && <polyline points="2.5,6.5 5.5,9.5 10.5,3.5" stroke="#fff" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />}
                        </svg>
                      </button>
                    );
                  })}
                </div>
                <p style={s.question}>Tell us about your project. <span style={s.optional}>Optional</span></p>
                <div style={{ ...s.fieldGroup, flex: 1, display: 'flex', flexDirection: 'column', marginBottom: 0 }}>
                  <textarea
                    ref={firstInputRef}
                    className="contact-input" style={{ ...s.input, ...s.textarea, flex: 1, resize: 'none' }}
                    placeholder="We're a local gym looking for a membership app with booking and push notifications..."
                    value={fields.description}
                    onChange={e => set('description', e.target.value)}
                  />
                </div>
              </div>
            )}

            {step === 2 && (
              <div style={{ display: 'flex', flexDirection: 'column', flex: 1 }}>
                <p style={s.question}>Any questions for us? <span style={s.optional}>Optional</span></p>
                <div style={{ ...s.fieldGroup, flex: 1, display: 'flex', flexDirection: 'column' }}>
                  <textarea
                    className="contact-input" style={{ ...s.input, ...s.textarea, flex: 1, resize: 'none' }}
                    placeholder="How long does a typical build take? Do you work with businesses outside the UK?..."
                    value={fields.questions}
                    onChange={e => set('questions', e.target.value)}
                  />
                </div>
                <p style={s.question}>Anything else? <span style={s.optional}>Optional</span></p>
                <div style={{ ...s.fieldGroup, marginBottom: 0 }}>
                  <textarea
                    className="contact-input" style={{ ...s.input, ...s.textarea }}
                    placeholder="Best time to call, budget range, anything else on your mind..."
                    value={fields.extra}
                    onChange={e => set('extra', e.target.value)}
                    rows={2}
                  />
                </div>
              </div>
            )}

            {status === 'error' && (
              <p style={s.errorBanner}>
                Something went wrong. Please try again or email us at <strong>{contactEmail()}</strong>
              </p>
            )}

            <div style={s.actions}>
              {step > 0 && (
                <button type="button" style={s.backBtn} onClick={() => setStep(s => s - 1)}>
                  Back
                </button>
              )}
              <button
                type="submit"
                style={{ ...s.submitBtn, opacity: status === 'submitting' ? 0.5 : 1, flex: 1 }}
                disabled={status === 'submitting'}
              >
                {status === 'submitting' ? 'Sending…' : step < STEPS.length - 1 ? 'Next' : 'Send message'}
              </button>
            </div>

          </form>
        </div>
      </section>
    </div>
  );
}

const s = {
  page: {
    minHeight: '100vh',
    background: '#f9f9f7',
    fontFamily: SANS,
    color: '#2a2a2a',
  },
  formSection: {
    background: '#0f0f13',
    padding: 'clamp(40px, 6vw, 72px) 24px clamp(64px, 8vw, 104px)',
  },
  formCard: {
    maxWidth: 600,
    margin: '0 auto',
    background: '#16161c',
    borderRadius: 18,
    border: '1px solid rgba(255,255,255,0.07)',
    padding: 'clamp(28px, 5vw, 48px)',
    minHeight: 580,
    boxSizing: 'border-box',
    position: 'relative',
    display: 'flex',
    flexDirection: 'column',
  },
  formTitle: {
    maxWidth: 600,
    margin: '0 auto',
    marginBottom: 28,
    textAlign: 'center',
  },
  formTitleEyebrow: {
    fontFamily: MONO,
    fontSize: '0.7rem',
    fontWeight: 500,
    letterSpacing: '0.12em',
    textTransform: 'uppercase',
    color: '#4a4f66',
    margin: '0 0 10px',
  },
  formTitleHeading: {
    fontSize: 'clamp(1.6rem, 4vw, 2.4rem)',
    fontWeight: 800,
    letterSpacing: '-0.03em',
    color: '#f0f0f5',
    margin: '0 0 10px',
  },
  formTitleSub: {
    fontSize: '0.9rem',
    color: '#6b7080',
    margin: 0,
  },
  stepHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 28,
  },
  skipBtn: {
    marginLeft: 'auto',
    background: 'none',
    border: 'none',
    color: 'rgba(255,255,255,0.5)',
    fontSize: '0.78rem',
    cursor: 'pointer',
    padding: 0,
    fontFamily: SANS,
  },
  stepCount: {
    fontFamily: MONO,
    fontSize: '0.72rem',
    fontWeight: 500,
    letterSpacing: '0.1em',
    textTransform: 'uppercase',
    color: 'rgba(255,255,255,0.4)',
  },
  stepTitle: {
    fontSize: '0.82rem',
    fontWeight: 600,
    color: '#818cf8',
  },
  dots: {
    display: 'flex',
    gap: 8,
    alignItems: 'center',
  },
  dot: {
    width: 8,
    height: 8,
    borderRadius: '50%',
    transition: 'background 0.3s',
  },
  question: {
    fontSize: '1rem',
    fontWeight: 600,
    color: '#d8dae8',
    margin: '0 0 10px',
  },
  optional: {
    fontWeight: 400,
    fontSize: '0.8rem',
    color: '#4a4f66',
  },
  fieldGroup: {
    marginBottom: 24,
    position: 'relative',
  },
  input: {
    width: '100%',
    background: '#0f0f13',
    border: '1px solid rgba(255,255,255,0.08)',
    borderRadius: 10,
    padding: '12px 14px',
    fontSize: '0.95rem',
    color: '#d8dae8',
    fontFamily: SANS,
    boxSizing: 'border-box',
    outline: 'none',
    transition: 'border-color 0.15s',
  },
  // The whole shorthand, not just borderColor: `input` above sets `border` as a
  // shorthand, and spreading a longhand over it leaves the field with no border
  // colour once the error clears — it then falls back to currentColor, which on
  // this dark input is near-white.
  inputError: { border: '1px solid rgba(248,113,113,0.5)' },
  textarea: {
    resize: 'vertical',
    lineHeight: 1.6,
    minHeight: 100,
  },
  fieldError: {
    position: 'absolute',
    top: '100%',
    left: 0,
    fontSize: '0.72rem',
    color: '#f87171',
    margin: '3px 0 0',
    fontFamily: MONO,
  },
  row2: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
    gap: 16,
  },
  chipRow: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: 8,
  },
  chipRowScroll: {
    display: 'flex',
    flexWrap: 'nowrap',
    gap: 8,
    overflowX: 'auto',
    WebkitOverflowScrolling: 'touch',
    paddingBottom: 4,
  },
  chip: {
    fontSize: '0.88rem',
    color: '#b0b4cc',
    cursor: 'pointer',
    background: '#0f0f13',
    border: '1px solid rgba(255,255,255,0.08)',
    borderRadius: 8,
    padding: '9px 16px',
    fontFamily: SANS,
    transition: 'border-color 0.15s, color 0.15s',
  },
  chipActive: {
    border: '1px solid rgba(129,140,248,0.5)',
    color: '#d8dae8',
  },
  actions: {
    display: 'flex',
    gap: 10,
    position: 'absolute',
    bottom: 'clamp(28px, 5vw, 48px)',
    left: 'clamp(28px, 5vw, 48px)',
    right: 'clamp(28px, 5vw, 48px)',
  },
  submitBtn: {
    background: '#818cf8',
    color: '#fff',
    border: 'none',
    borderRadius: 10,
    fontSize: '0.95rem',
    fontWeight: 600,
    padding: '13px 20px',
    cursor: 'pointer',
    fontFamily: SANS,
    transition: 'opacity 0.15s',
  },
  backBtn: {
    padding: '13px 18px',
    background: 'rgba(255,255,255,0.05)',
    color: '#b0b4cc',
    border: '1px solid rgba(255,255,255,0.08)',
    borderRadius: 10,
    fontSize: '0.95rem',
    fontWeight: 500,
    cursor: 'pointer',
    fontFamily: SANS,
  },
  errorBanner: {
    marginBottom: 16,
    padding: '12px 16px',
    background: 'rgba(248,113,113,0.08)',
    border: '1px solid rgba(248,113,113,0.25)',
    borderRadius: 10,
    fontSize: '0.84rem',
    color: '#f87171',
    lineHeight: 1.6,
  },
  successBox: {
    textAlign: 'center',
    padding: 'clamp(32px, 6vw, 64px) 0',
  },
  successIcon: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: 56,
    height: 56,
    borderRadius: '50%',
    background: 'rgba(52,211,153,0.12)',
    border: '1px solid rgba(52,211,153,0.3)',
    color: '#34d399',
    fontSize: '1.4rem',
    margin: '0 auto 24px',
  },
  successHeadline: {
    fontSize: 'clamp(1.6rem, 4vw, 2.4rem)',
    fontWeight: 800,
    letterSpacing: '-0.03em',
    color: '#f0f0f5',
    margin: '0 0 12px',
  },
  successSub: {
    fontSize: '0.96rem',
    color: '#6b7080',
    lineHeight: 1.7,
    margin: '0 0 32px',
  },
  resetBtn: {
    background: 'transparent',
    border: '1px solid rgba(255,255,255,0.12)',
    color: '#6b7080',
    borderRadius: 8,
    padding: '10px 20px',
    fontSize: '0.84rem',
    cursor: 'pointer',
    fontFamily: SANS,
  },
};

export default ContactScreen;
